import React, {
  InputHTMLAttributes,
  RefObject,
  useEffect,
  useRef,
} from "react";
import { useSchema } from "../ContractContext";
import {
  InputFocus,
  useInputFocus,
} from "../validation/InputValidationContext";
import InputMask from "react-input-mask";
import { NumberInput } from "./NumberInput";
import { FieldSpecification } from "./ContractFieldInput";
import { DateString } from "../model/ContractViewModel";
import { DateInput } from "@teamscheme/date-input";
import PhoneInput from "@teamscheme/phone-input";

export type FieldValueType = string | number | boolean | DateString;
export interface FieldValueProps<TValue extends FieldValueType> {
  current: TValue | null;
  onChange(value: TValue | null): void;
  onSave(value: TValue | null): Promise<void>;
  options?: { label: string; value: TValue }[];
}

export type TypedFieldValueProps =
  | { type: "account"; value: FieldValueProps<string> }
  | { type: "phone"; value: FieldValueProps<string> }
  | { type: "text"; value: FieldValueProps<string> }
  | { type: "tel"; value: FieldValueProps<string> }
  | { type: "email"; value: FieldValueProps<string> }
  | { type: "textarea"; value: FieldValueProps<string> }
  | { type: "date"; value: FieldValueProps<DateString> }
  | { type: "decimal"; value: FieldValueProps<number> }
  | { type: "int"; value: FieldValueProps<number> };

export type FieldType = TypedFieldValueProps["type"];

export interface FieldValuePropsBase extends FieldSpecification {
  required?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  hasError?: boolean;
}

export type FieldInputProps = FieldValuePropsBase & TypedFieldValueProps;

export function limitLines(
  str: string | null,
  maxLines = 4,
  maxCharsPerLine = 76
) {
  if (!str) return str;

  const lines = str.split("\n").filter((line) => !!line);
  const pivot = Math.max(0, maxLines - 1);
  const head = lines.slice(0, pivot);
  const tail = lines.slice(pivot);

  return [...head, tail.join(" ")].join("\n");
}

export function useConsumeFocus<T extends { focus(): void }>(
  focusable: RefObject<T | null>,
  focus?: InputFocus
) {
  useEffect(() => {
    if (focus && focusable.current) {
      focusable.current.focus();
      focus.consume();
    }
  }, [focusable, focus]);
}

export function FieldInput(props: FieldInputProps) {
  const schema = useSchema();
  const { section, name, required, inputProps } = props;
  const placeholder = schema.getFieldPlaceholder(section, name);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const focus = useInputFocus(section, name);
  const id = `${section}.${name}`;

  useEffect(() => {
    if (focus) {
      if (inputRef?.current) inputRef.current.focus();
      if (selectRef?.current) selectRef.current.focus();
      if (textAreaRef?.current) textAreaRef.current.focus();

      focus.consume();
    }
  }, [focus]);

  if (props.type === "account") {
    const { current, onChange, onSave } = props.value;

    return (
      <InputMask
        inputRef={(input) => (inputRef.current = input)}
        name={name}
        id={id}
        type="tel"
        value={typeof current === "string" ? current : ""}
        placeholder={placeholder as string}
        onChange={(event) => onChange(event.target.value || null)}
        onBlur={() => onSave(current)}
        required={required}
        mask="9999.99.99999"
        maskChar={null}
        {...inputProps}
      />
    );
  }

  if (props.type === "phone") {
    const { current, onChange, onSave } = props.value;
    return (
      <PhoneInput
        ref={inputRef}
        value={current || ""}
        onChange={(x) => onChange(x === "" ? null : x)}
        hasError={props.hasError}
        onSave={(value) => {
          if (onSave) onSave(value);
        }}
        inputProps={{
          ...inputProps,
          name,
          id,
          required,
        }}
      />
    );
  }

  if (props.type === "text" || props.type === "tel" || props.type === "email") {
    const { current, onChange, onSave } = props.value;

    return (
      <input
        ref={inputRef}
        name={name}
        id={id}
        type={props.type}
        value={
          current === null || current === undefined ? "" : current.toString()
        }
        placeholder={placeholder as string}
        onChange={(event) => onChange(event.target.value || null)}
        onBlur={() => onSave(current)}
        required={required}
        {...inputProps}
      />
    );
  }

  if (props.type === "int") {
    const { current, onChange, onSave, options } =
      props.value as FieldValueProps<number>;

    if (options) {
      return (
        <select
          ref={selectRef}
          name={name}
          id={id}
          required={required}
          onChange={async (event) => {
            const { value: rawValue } = event.target;
            const value = rawValue === "" ? null : ~~rawValue;

            onChange(value);

            await onSave(value);
          }}
          value={current === null ? "" : current}
        >
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map(({ value, label }) => (
            <option key={value} value={value}>
              {label || value}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        ref={inputRef}
        name={name}
        id={id}
        type="tel"
        value={typeof current === "number" ? current.toString() : ""}
        placeholder={placeholder as string}
        onChange={(event) =>
          onChange(
            event.target.value !== "" ? parseInt(event.target.value) : null
          )
        }
        onBlur={() => onSave(current)}
        required={required}
        {...inputProps}
      />
    );
  }

  if (props.type === "decimal") {
    const { current, onChange, onSave } = props.value;

    return (
      <NumberInput
        {...inputProps}
        inputRef={inputRef}
        name={name}
        id={id}
        value={current}
        onChange={(next: number | null) => onChange(next)}
        onBlur={() => onSave(current)}
        placeholder={placeholder}
        required={required}
      />
    );
  }

  if (props.type === "date") {
    const { current, onChange, onSave } = props.value;

    return (
      <DateInput
        {...inputProps}
        ref={inputRef}
        name={name}
        id={id}
        value={current === null ? null : new Date(current)}
        onChange={(next) =>
          onChange(next ? (next.toISOString() as DateString) : null)
        }
        onBlur={() => onSave(current)}
        required={required}
      />
    );
  }

  if (props.type === "textarea") {
    const { current, onChange, onSave } = props.value;

    return (
      <textarea
        ref={textAreaRef}
        name={name}
        id={id}
        value={current || ""}
        rows={4}
        placeholder={placeholder as string}
        onChange={(event) => onChange(event.target.value || null)}
        onBlur={() => {
          onSave(limitLines(current));
        }}
        required={required}
      />
    );
  }

  return null;
}
